import { useNodeConnections, useReactFlow } from '@xyflow/react';
import { memo, useEffect } from 'react';
import { useI18n } from '@/providers/i18n';
import { VideoPrimitive } from './primitive';
import { VideoTransform } from './transform';
import { areNodePropsEqual } from '../are-node-props-equal';
import { useWhyNodeRender, usePerfSentinel } from '@/lib/debug/use-perf';
import { getIsCanvasPositionInteractingSync } from '@/components/canvas/utils/drag-state-store';

export type VideoNodeProps = {
  type: string;
  data: {
    content?: {
      url: string;
      type: string;
    };
    /** 原始导入的视频 URL（通过工具栏输入的 URL） */
    sourceUrl?: string;
    generated?: {
      url: string;
      type: string;
    };
    updatedAt?: string;
    model?: string;
    instructions?: string;
    width?: number;
    height?: number;
    /** 节点视图模式：transform 或 primitive */
    viewMode?: 'transform' | 'primitive';
  };
  id: string;
};

export const VideoNode = memo((props: VideoNodeProps) => {
  const __why = useWhyNodeRender('VideoNode', props);
  usePerfSentinel(
    'videoNodeRenderDuringDrag',
    getIsCanvasPositionInteractingSync(),
    __why,
  );
  const { t } = useI18n();
  const { updateNodeData } = useReactFlow();
  const connections = useNodeConnections({
    id: props.id,
    handleType: 'target',
  });
  const existingViewMode = (props.data as any)?.viewMode;
  
  // 一旦节点变成 transform，就永久保持 transform，不会变回 primitive
  const isTransform = existingViewMode === 'transform' || connections.length > 0;
  const Component = isTransform ? VideoTransform : VideoPrimitive;

  // 当节点首次变成 transform 时，保存 viewMode；若已有 content 且尚无 generated，则将 content 复制到 generated
  // Perf (Phase 9.4): primitive deps instead of `props.data` whole object.
  const dataContentUrl = (props.data as any)?.content?.url;
  const dataContentType = (props.data as any)?.content?.type;
  const dataGeneratedUrl = (props.data as any)?.generated?.url;
  useEffect(() => {
    if (!isTransform) return;
    const hasGenerated = Boolean(dataGeneratedUrl);
    if (existingViewMode !== 'transform') {
      const updates: any = { viewMode: 'transform' };
      if (dataContentUrl && !hasGenerated) {
        updates.generated = { url: dataContentUrl, type: dataContentType };
      }
      updateNodeData(props.id, updates);
    } else if (dataContentUrl && !hasGenerated) {
      updateNodeData(props.id, {
        generated: { url: dataContentUrl, type: dataContentType },
      });
    }
  }, [
    props.id,
    isTransform,
    existingViewMode,
    dataContentUrl,
    dataContentType,
    dataGeneratedUrl,
    updateNodeData,
  ]);

  return <Component {...props} title={t('Video Node')} />;
}, areNodePropsEqual);
VideoNode.displayName = 'VideoNode';
